/**
 * PATH: packages/ui/src/kanban/surfaceTokens.ts
 * FILE_ID: PKG-UI-KANBAN-SURFACE-TOKENS
 * PURPOSE: Single source of truth for the Kanban board's *surface* literals —
 *          card elevation tiers, explicit-state rings, and semantic edge
 *          stripes. One place to read so the staged visual lift (see
 *          docs/plans/KANBAN_VISUAL_LIFT_PLAN.md) can re-skin every hub from a
 *          single edit instead of scattered inline strings.
 * WHY: KanbanCard composed its entire box-shadow inline (accent stripe + state
 *      ring + elevation in one CSS property). Centralising the values + the
 *      resolution order here is Stage 0 of the lift — byte-identical output,
 *      so it ships at zero visual risk and turns Stage 2 (card polish) into a
 *      one-edit change.
 * FLOW:
 *   ┌───────────────┐   ┌──────────────────┐   ┌────────────────────┐
 *   │ card state in  │ → │ cardElevation /  │ → │ composed boxShadow  │
 *   │ (drag/hover/…) │   │ cardRing resolve │   │ string out          │
 *   └───────────────┘   └──────────────────┘   └────────────────────┘
 * RELATED:
 *   - components/KanbanCard.tsx: the consumer (Stage 0 + 2)
 *   - surfaceTokens.test.ts: pins every branch returns the legacy string
 *
 * HISTORY: Stage 0 (2026-06-19) extracted these byte-identical from the
 * 2026-05-30 uplift. Stage 2 (2026-06-20) re-tuned the values for the calm
 * card surface: a single soft resting shadow, a gentler hover lift, and the
 * accent moved from an inset box-shadow stripe to a real `border-left`.
 * surfaceTokens.test.ts is the contract — keep it in lock-step with any edit.
 */

/**
 * Card drop-shadow elevation by interaction state (resting / hover / drag).
 * Stage 2: a single soft resting layer (was a doubled 0.06/0.05 stack) and a
 * calmer hover/drag lift so a board of cards reads quiet, not embossed.
 */
export const CARD_ELEVATION = {
  rest: '0 1px 2px 0 rgba(16,24,40,0.05)',
  hover: '0 6px 16px -8px rgba(16,24,40,0.22)',
  drag: '0 12px 28px -10px rgba(16,24,40,0.38)',
} as const

/**
 * Interaction-state rings. Each ring is *behaviour feedback*, not decoration —
 * preserve all of them. Implemented as `box-shadow` so they layer with the
 * accent stripe + elevation in the single `box-shadow` CSS property.
 */
export const CARD_RING = {
  find: '0 0 0 3px rgb(234 88 12)', // orange-600 — active board-find hit (⌘F)
  // blue-600 — multi-select member (FEAT-2026-06-15). BUG-2026-06-29: bumped
  // from a 2px ring to a 3px ring + a soft outer glow so a selected card is
  // unmistakable across a dense board (operator: "the blue tick is not good").
  // Paired with a tinted body overlay (KanbanCardOverlays) for a full
  // highlight, not just an edge.
  selected: '0 0 0 3px rgb(37 99 235), 0 0 0 6px rgba(37,99,235,0.22)',
  allocate: '0 0 0 2px rgb(16 185 129)', // emerald-500 — allocate-email target / just-allocated flash
  nest: '0 0 0 2px rgb(139 92 246)', // violet-500 — nest-under target
  drag: '0 0 0 2px rgba(96,165,250,0.70)', // blue-400 — in-flight
  blocked: '0 0 0 1px rgba(239,68,68,0.40)', // red-500 — blocked
  none: '',
} as const

export interface CardElevationState {
  dragging: boolean
  hovered: boolean
  emailDropHover: boolean
  nestDropHover: boolean
}

/** Resolve the elevation shadow for a card's interaction state. */
export function cardElevation(s: CardElevationState): string {
  if (s.dragging) return CARD_ELEVATION.drag
  if (s.hovered && !s.emailDropHover && !s.nestDropHover) return CARD_ELEVATION.hover
  return CARD_ELEVATION.rest
}

export interface CardRingState {
  isActiveFindHit: boolean
  selected: boolean
  emailDropHover: boolean
  justAllocated: boolean
  nestDropHover: boolean
  dragging: boolean
  hovered: boolean
  blocked: boolean
}

/**
 * Resolve the state ring for a card. Hover gets elevation only: a blue outline
 * duplicates selection feedback and makes an ordinary card read as erroneous.
 * Precedence: find → selected → allocate → nest → drag → blocked.
 */
export function cardRing(s: CardRingState): string {
  if (s.isActiveFindHit) return CARD_RING.find
  if (s.selected) return CARD_RING.selected
  if (s.emailDropHover || s.justAllocated) return CARD_RING.allocate
  if (s.nestDropHover) return CARD_RING.nest
  if (s.dragging) return CARD_RING.drag
  if (s.blocked) return CARD_RING.blocked
  return CARD_RING.none
}

/** Width (px) of the card's accent border. */
export const CARD_ACCENT_BORDER_WIDTH = 3

/**
 * The accent — a real `border` shorthand as of Stage 2 (was an `inset` box-shadow
 * stripe). A genuine border renders crisper at the rounded corners, survives
 * print/export, and frees the box-shadow property for pure elevation +
 * interaction rings. The value is side-neutral; the card root applies it to the
 * RIGHT edge via inline `style.borderRight` (operator preference 2026-06-29 —
 * the accent reads as a colour tab on the right of the card, clear of the
 * left-aligned title) and adds ~1px of right padding to keep content aligned.
 */
export function cardAccentBorder(accent: string): string {
  return `${CARD_ACCENT_BORDER_WIDTH}px solid ${accent}`
}

/**
 * FEAT-2026-06-29-KANBAN-CARD-PRIORITY-EDGE — a LEFT-edge colour bar that
 * surfaces card priority on the resting tile (previously priority was invisible
 * until you opened the hover-peek or the drawer).
 *
 * FEAT-2026-06-29-KANBAN-CARD-HOVER-MENU — operator: *"the side colour is the
 * Priority"*. The left edge is now the PRIMARY priority signal, so every
 * explicitly-set level paints a bar (colours mirror the right-click priority
 * submenu so the two stay in lock-step):
 *
 *   3 = Urgent → red-500   (#ef4444)
 *   2 = High   → amber-500 (#f59e0b)
 *   1 = Normal → no bar
 *   0 = Low → no bar
 *
 * Normal and Low deliberately stay neutral: a coloured edge is reserved for
 * escalation, not a default work item.
 *
 * The RIGHT edge stays the identity/label accent (`cardAccentBorder`, which
 * prefers the card's first label colour), so priority claims the left. Width
 * mirrors the accent (`CARD_ACCENT_BORDER_WIDTH`) for visual symmetry. Returns
 * `undefined` for Low so the caller falls through to the themed 1px `border`
 * className on the left.
 */
export function cardPriorityBorder(priority: number): string | undefined {
  if (priority >= 3) return `${CARD_ACCENT_BORDER_WIDTH}px solid #ef4444`
  if (priority === 2) return `${CARD_ACCENT_BORDER_WIDTH}px solid #f59e0b`
  return undefined
}

/**
 * Compose the card `box-shadow`: state ring + elevation, dropping empty
 * layers. Stage 2 removed the accent stripe from here — it is now a
 * `border` (see cardAccentBorder), so the box-shadow carries only
 * depth + interaction feedback.
 */
export function composeCardBoxShadow(ring: string, elevation: string): string {
  return [ring, elevation].filter(Boolean).join(', ')
}

/**
 * Lane resting elevation. NOTE: KanbanLane applies this as a Tailwind *arbitrary*
 * class (`shadow-[…]`) which must stay a literal in the className for the JIT
 * compiler to emit it — so this export is the documented single source for the
 * VALUE only; keep KanbanLane's class string in sync with it. Not consumed at
 * runtime (intentionally) to avoid a dead Tailwind class.
 */
export const LANE_REST_SHADOW_VALUE =
  '0 1px 2px rgba(16,24,40,0.04), 0 14px 30px -20px rgba(16,24,40,0.5)'
